import keycloak from "@/lib/keycloak";
import type { Order } from "@/types/order";
import type {
  AdminCaseDetail,
  AdminCaseListItem,
  AdminEvidence,
  AdminRemovalRequest,
  EvidenceType,
  RemovalStatus,
  Site,
  SiteInput,
} from "@/types/admin";
import axios from "axios";

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: { "Content-Type": "application/json" },
});

apiClient.interceptors.request.use((config) => {
  if (keycloak.authenticated && keycloak.token) {
    config.headers.Authorization = `Bearer ${keycloak.token}`;
  }
  return config;
});

export async function subscribe(
  emailAddress: string,
  subscriberType: "personal" | "business"
) {
  await apiClient.post(`/subscribe/${subscriberType}`, { emailAddress });
}

export async function createOrder(couponCode?: string): Promise<string> {
  const response = await apiClient.post<{ url: string }>("/orders", {
    successUrl: `${window.location.origin}/dashboard?payment=success`,
    cancelUrl: `${window.location.origin}/dashboard?payment=cancelled`,
    couponCode,
  });
  return response.data.url;
}

export async function getOrders(): Promise<Order[]> {
  const response = await apiClient.get<Order[]>("/orders");
  return response.data;
}

// Reconcile an order after checkout in case its webhook was missed. Idempotent.
export async function confirmOrder(sessionId: string): Promise<string> {
  const response = await apiClient.post<{ status: string }>("/orders/confirm", {
    sessionId,
  });
  return response.data.status;
}

// --- BankID: signing the fullmakt ---

export interface SignStart {
  ref: string;
  autoStartToken: string;
  signedText: string;
}

export interface SignStatus {
  status: "pending" | "complete" | "failed";
  rfa: string;
  hintCode?: string;
}

export async function getFullmaktStatus(): Promise<{
  hasCase: boolean;
  signed: boolean;
  signedAt: string | null;
}> {
  const response = await apiClient.get("/bankid/fullmakt");
  return response.data;
}

export async function startFullmaktSign(): Promise<SignStart> {
  const response = await apiClient.post<SignStart>("/bankid/sign");
  return response.data;
}

export async function getSignQr(ref: string): Promise<string> {
  const response = await apiClient.get<{ qrData: string }>(
    `/bankid/sign/${ref}/qr`,
  );
  return response.data.qrData;
}

export async function getSignStatus(ref: string): Promise<SignStatus> {
  const response = await apiClient.get<SignStatus>(
    `/bankid/sign/${ref}/collect`,
  );
  return response.data;
}

export async function cancelSign(ref: string): Promise<void> {
  await apiClient.post(`/bankid/sign/${ref}/cancel`);
}

// --- Admin ---

export async function adminGetCases(params?: {
  status?: string;
  q?: string;
}): Promise<AdminCaseListItem[]> {
  const response = await apiClient.get<AdminCaseListItem[]>("/admin/cases", {
    params,
  });
  return response.data;
}

export async function adminGetCase(id: string): Promise<AdminCaseDetail> {
  const response = await apiClient.get<AdminCaseDetail>(`/admin/cases/${id}`);
  return response.data;
}

export async function adminUpdateRemovalRequest(
  id: string,
  body: { status?: RemovalStatus; notes?: string | null },
): Promise<AdminRemovalRequest> {
  const response = await apiClient.patch<AdminRemovalRequest>(
    `/admin/removal-requests/${id}`,
    body,
  );
  return response.data;
}

export async function adminRevealPersonnummer(
  caseId: string,
): Promise<{ personnummer: string }> {
  const response = await apiClient.post<{ personnummer: string }>(
    `/admin/cases/${caseId}/reveal-personnummer`,
  );
  return response.data;
}

export async function adminUploadEvidence(
  caseId: string,
  file: File,
  type: EvidenceType,
  removalRequestId?: string,
): Promise<AdminEvidence> {
  const form = new FormData();
  form.append("file", file);
  form.append("type", type);
  if (removalRequestId) form.append("removalRequestId", removalRequestId);
  const response = await apiClient.post<AdminEvidence>(
    `/admin/cases/${caseId}/evidence`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response.data;
}

export async function adminDownloadEvidence(
  id: string,
  filename: string,
): Promise<void> {
  const response = await apiClient.get(`/admin/evidence/${id}/download`, {
    responseType: "blob",
  });
  const url = URL.createObjectURL(response.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function adminGetSites(): Promise<Site[]> {
  const response = await apiClient.get<Site[]>("/admin/sites");
  return response.data;
}

export async function adminCreateSite(body: SiteInput): Promise<Site> {
  const response = await apiClient.post<Site>("/admin/sites", body);
  return response.data;
}

export async function adminUpdateSite(
  id: string,
  body: Partial<SiteInput>,
): Promise<Site> {
  const response = await apiClient.patch<Site>(`/admin/sites/${id}`, body);
  return response.data;
}

export async function adminDeleteSite(id: string): Promise<void> {
  await apiClient.delete(`/admin/sites/${id}`);
}
